import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { IVorRecord, VorRecordStatus } from "@components/resourceManagement/vor/vor.interfaces";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import { DateService } from "./date.service";

export class VorService {

    private /*readonly*/ dateService = new HybridServiceAccessor<DateService>("dateService", this.$injector).service;

    updatedVorRecord: IVorRecord = null;
    deletedVorId = 0;

    static $inject = ["$q", "$http", "translationService", "$injector"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly translationService: TranslationService,
        private readonly $injector: angular.auto.IInjectorService) {
    }

    permanentDate = new Date(Date.UTC(2200, 1, 1));

    checkForOverlappingVorRecords(vorRecord: IVorRecord, allRecords: IVorRecord[]): boolean {
        const { Id, StartDate, EndDate } = vorRecord;
        const vorEnd = EndDate || this.permanentDate;
    
        return allRecords.some(record => {
            if (record.Id === Id) return false;
    
            const recordStart = record.StartDate;
            const recordEnd = record.EndDate || this.permanentDate
    
            return this.dateService.date1IsAfterDate2(vorEnd, recordStart, true) && this.dateService.date1IsAfterDate2(recordEnd, StartDate, true);
        });
    }

    getCurrentRecords(invalidateCache = false): angular.IPromise<IVorRecord[]> {
        return this.$http({
            url: `/api/vor/current/`,
            method: "GET",
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        })
            .then((response: angular.IHttpResponse<IVorRecord[]>) => {
                const list: IVorRecord[] = [];

                response.data.forEach(r => list.push(this.mapToUi(r)));

                return list;
            });
    }

    getCurrentAndFutureVorRecords(invalidateCache = false): angular.IPromise<IVorRecord[]> {
        return this.$http({
            url: `/api/vor/future/`,
            method: "GET",
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        })
            .then((response: angular.IHttpResponse<IVorRecord[]>) => {
                const list: IVorRecord[] = [];

                response.data.forEach(r => list.push(this.mapToUi(r)));

                return list;
            });
    }

    getVehicleRecords(vehicleId: number, mapToUi = true, invalidateCache = false): angular.IPromise<IVorRecord[]> {
        return this.$http({
            url: `/api/vor/vehicle/${vehicleId}/`,
            method: "GET",
            params: { minsToLive: 30, invalidateCache: invalidateCache }
        })
            .then((response: angular.IHttpResponse<IVorRecord[]>) => {

                if (!mapToUi) {
                    return response.data;
                }

                const list: IVorRecord[] = [];

                response.data.forEach(r => list.push(this.mapToUi(r)));

                return list;
            });
    }

    deleteRecord(id: number): angular.IPromise<boolean> {
        this.deletedVorId = id;
        this.updatedVorRecord = null;

        return this.$http({
            url: `/api/vor/delete/${id}/`,
            method: "GET"
        })
            .then(() => {
                return this.invalidateVorCache(id).then(() => {
                    return true;
                });
            });
    }

    invalidateVorCache(id: number): angular.IPromise<boolean> {
        const getAllRecordsPromise = this.getCurrentRecords(true);

        return this.$q.all([getAllRecordsPromise]).then(() => {
            return true;
        });
    }

    saveRecord(vorRecord: IVorRecord): angular.IPromise<IVorRecord> {
        return this.$http({
            url: `/api/vor/save/`,
            data: this.mapFromUi(vorRecord),
            method: "POST"
        })
            .then((response: angular.IHttpResponse<IVorRecord>) => {
                return this.mapToUi(response.data);
            })
    }

    updateOrCreateCachedRecord(index: number, vorRecords: IVorRecord[]) {
        if (index >= 0) {
            vorRecords.splice(index, 1, angular.copy(this.updatedVorRecord));
        } else {
            vorRecords.push(angular.copy(this.updatedVorRecord));
        }
    }

    updateCachedData(vorRecords: IVorRecord[]) {
        if (this.updatedVorRecord) {
            // extra getIndex code needed here because zero Id is returned from db on creation of Vor record
            const index = this.updatedVorRecord.Id > 0 ? this.getVorIndex(this.updatedVorRecord.Id, vorRecords) : this.getVorIndexOnCreate(this.updatedVorRecord, vorRecords);
            this.updateOrCreateCachedRecord(index, vorRecords);
            this.updatedVorRecord = null;
        }

        if (this.deletedVorId) {
            const index = this.getVorIndex(this.deletedVorId, vorRecords);
            if (index >= 0) {
                vorRecords.splice(index, 1);
            }
            this.deletedVorId = 0;
        }
    }

    private mapToUi(vorRecord: IVorRecord): IVorRecord {
        if (!this.dateService) {
            // Race condition quirk
            this.dateService = new HybridServiceAccessor<DateService>("dateService", this.$injector).service;
        }

        vorRecord.StartDate = new Date(vorRecord.StartDate);

        if (this.dateService.isSameDate(vorRecord.EndDate, this.permanentDate)) {
            vorRecord.EndDate = null;
        }
        else {
            vorRecord.EndDate = new Date(vorRecord.EndDate);
        }

        this.setVorStatus(vorRecord);

        return vorRecord;
    }

    private mapFromUi(vorRecord: IVorRecord): IVorRecord {
        const recordToSave = angular.copy(vorRecord);

        if (!recordToSave.EndDate) {
            recordToSave.EndDate = this.permanentDate;
        }

        return recordToSave;
    }

    private getVorIndexOnCreate(vorRecord: IVorRecord, vorRecords: IVorRecord[]): number {
        for (let i = 0; i < vorRecords.length; i++) {
            const record = vorRecords[i];
            if (record.VehicleId === vorRecord.VehicleId && record.Description === vorRecord.Description) {
                return i;
            }
        }

        return -1;
    }

    private getVorIndex(id: number, vorRecords: IVorRecord[]): number {
        for (let i = 0; i < vorRecords.length; i++) {
            if (vorRecords[i].Id === id) {
                return i;
            }
        }

        return -1;
    }

    setVorStatus(vorRecord: IVorRecord) {
        const now = new Date();

        if (vorRecord.EndDate && this.dateService.date1IsAfterDate2(now, vorRecord.EndDate)) {
            vorRecord.status = VorRecordStatus.Completed;
            vorRecord.vorStatus = this.translationService.t("vorService.completed");
        }
        else if (this.dateService.date1IsAfterDate2(vorRecord.StartDate, now)) {
            vorRecord.status = VorRecordStatus.Pending;
            vorRecord.vorStatus = this.translationService.t("vorService.pending");
        }
        else {
            vorRecord.status = VorRecordStatus.Active;
            vorRecord.vorStatus = this.translationService.t("vorService.active");
        }
    }
}
